<script setup>
import {ref, shallowRef, onMounted, onBeforeUnmount, inject, useTemplateRef, nextTick} from 'vue';
import {useI18n} from 'vue-i18n';
import formSerialize from 'form-serialize';

import {QUERY_SAVE, CONFIRM} from '@/core/constants/modals';
import {useStore} from '@/core/plugins/store';
import {useChartsRepository} from '@/core/repository/charts';
import {getParamValue, removeParams} from '@/core/helpers/urlParams';
import {useQueryRepository} from '@/core/repository/query';
import {useQueryBus} from '@/core/plugins/queryBus';
import {QUERY_ACTIONS, CHART_TYPE} from '@/core/constants/query';
import {useMutationObserver} from '@/core/composables/useMutationObserver';
import SavedChartQuery from '@/components/Filters/SavedChartQuery.vue';

const props = defineProps({
    chartType: {
        type: String,
        default: () => CHART_TYPE.CFD,
    },
});

const emits = defineEmits([
    'query-change',
    'query-reset',
]);

const bus = inject('bus');

const filterOptions = window.filter_options;
const currentPath = window.location.pathname;

const addFilterSelectRef = useTemplateRef('addFilterSelectRef');
const formRef = useTemplateRef('formRef');
const filterTableRef = useTemplateRef('filterTableRef');
const isLoading = shallowRef(false);
const currentQueryId = shallowRef(null);
const hasUnselectFilters = shallowRef(false);
const currentQuery = ref(false);

const {t} = useI18n();
const {setValue} = useStore();
useMutationObserver(filterTableRef, checkUnselectFilters, {attributeFilter: ['style']});

function updateCurrentQueryId() {
    currentQueryId.value = getParamValue('query_id');
}

function handleAddNew() {
    handleReset();
    handleSave(QUERY_ACTIONS.SAVE);
}

async function getFormData() {
    isLoading.value = true;
    if (!formRef.value) return;
    
    const serializedData = formSerialize(formRef.value, {hash: true});
    const payload = {
        set_filter: 1,
        ...serializedData,
    };

    const data = await useChartsRepository(props.chartType).getChartData(payload);
    isLoading.value = false;
    setValue(data);
}

async function handleSave(action = QUERY_ACTIONS.SAVE) {
    const formData = new FormData(formRef.value);
    formData.append('chart_type', props.chartType);
    
    bus.emit('modalShow', {
        template: QUERY_SAVE,
        queryData: formData,
        action,
        callback: action === QUERY_ACTIONS.EDIT ? getFormData : undefined,
    });
}

async function handleDelete() {
    const query_id = getParamValue('query_id');

    if (!query_id) {
        return;
    }

    await useQueryRepository().deleteQuery(query_id);
    handleReset();
}

function handleReset() {
    removeParams();
    filterTableRef.value.innerHTML = '';
    Array.from(addFilterSelectRef.value.querySelectorAll('option')).forEach((option) => {
        option.removeAttribute('disabled');
    });
    getFormData();
    useQueryBus().listUpdate();
    emits('query-reset');
}

function handleFilterChange(filterQuery) {
    if (!filterQuery?.filters) return;

    const filters = Object.keys(filterQuery.filters);
    currentQuery.value = filterQuery;
    
    Array.from(addFilterSelectRef.value.querySelectorAll('option')).forEach((option) => {
        if (filters.includes(option.value)) {
            option.setAttribute('disabled', 'disabled');
        } else {
            option.removeAttribute('disabled');
        }
    });

    emits('query-change', currentQuery.value);

    nextTick().then(getFormData);
}

function handleClear() {
    Array.from(filterTableRef.value.querySelectorAll('.filter')).forEach(($filter) => {
        const $checkbox = $filter.querySelector('.field input[type="checkbox"]');
        const isChecked = $checkbox.checked;

        if (!isChecked) {
            document.querySelector(`#add_filter_select option[value="${$checkbox.value}"]`).removeAttribute('disabled');
            filterTableRef.value.removeChild($filter);
        }
    });
}

function checkUnselectFilters() {
    hasUnselectFilters.value = Array.from(
        filterTableRef.value.querySelectorAll('.filter > .field input[type="checkbox"]'),
    ).some(($checkbox) => !$checkbox.checked);
}

onMounted(() => {
    window.initVueFilters();
    updateCurrentQueryId();
    checkUnselectFilters();

    window.addEventListener('updatestate', updateCurrentQueryId);
});

onBeforeUnmount(() => {
    window.removeEventListener('updatestate', updateCurrentQueryId);
});
</script>

<template>
    <form
        ref="formRef"
        class="chart-filter"
        @submit.prevent="handleSubmit"
    >
        <div class="chart-filter__wrapper">
            <div class="add-filter">
                <div class="chart-filter__block table-header">
                    <label class="chart-filter__label">
                        {{ t('filters.label_filter_add') }}
                    </label>
                    <select
                        id="add_filter_select"
                        ref="addFilterSelectRef"
                        v-html="filterOptions"
                    />
                </div>
            </div>

            <div class="chart-filter-list">
                <div class="chart-filter__buttons chart-filter__buttons--top">
                    <SavedChartQuery
                        :chart-type="chartType"
                        @change="handleFilterChange"
                    />
                    <template v-if="currentQueryId">
                        <a
                            href="#"
                            class="chart-filter__buttons-btn pt-4 pb-4"
                            :title="$t('filters.label_add_new')"
                            @click.prevent="() => handleAddNew()"
                        >
                            <span class="icon icon-add" />
                        </a>
                        <a
                            href="#"
                            :title="$t('label_rename')"
                            class="chart-filter__buttons-btn"
                            @click.prevent="handleSave(QUERY_ACTIONS.RENAME);"
                        >
                            <span class="icon icon-edit" />
                        </a>
                        <a
                            href="#"
                            :title="$t('label_delete')"
                            class="chart-filter__buttons-btn"
                            @click.prevent="bus.emit('modalShow', {
                                template: CONFIRM,
                                text: $t('filters.label_confirm_delete_filter'),
                                callback: handleDelete,
                            });"
                        >
                            <span class="icon icon-del" />
                        </a>
                        <a
                            :href="currentPath"
                            :title="$t('label_reset')"
                            class="chart-filter__buttons-btn"
                            @click.prevent="handleReset"
                        >
                            <span class="icon icon-reload" />
                        </a>
                    </template>
                </div>
                <table
                    id="filters-table"
                    ref="filterTableRef"
                    class="chart-filter__table"
                />
                <slot
                    name="custom-filters"
                    :query="currentQuery"
                />
                <div class="chart-filter__buttons">
                    <a
                        href="#"
                        class="icon icon-checked"
                        @click.prevent="getFormData"
                    >
                        {{ $t('label_apply') }}
                    </a>
                    <a
                        href="#"
                        class="icon icon-save"
                        @click.prevent="() => handleSave(!currentQueryId ? QUERY_ACTIONS.SAVE : QUERY_ACTIONS.EDIT)"
                    >
                        {{ $t(!currentQueryId ? 'label_save' : 'label_save_changes') }}
                    </a>
                    <a
                        v-if="hasUnselectFilters"
                        href="#"
                        class="icon icon-del"
                        @click.prevent="handleClear"
                    >
                        {{ $t('label_clear') }}
                    </a>
                </div>
            </div>
        </div>
    </form>
    <div
        v-if="isLoading"
        class="loader"
    >
        Loading...
    </div>
</template>

<style lang="scss" scoped>
.chart-filter {
    --button-background: var(--input-color);

    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    flex-wrap: wrap;
    margin-bottom: 30px;

    @media screen and (max-width: 1200px) {
        flex-direction: column;
    }

    &__wrapper {
        display: flex;
        flex-direction: row-reverse;
        align-items: flex-start;
        justify-content: space-between;
        gap: 8px 16px;
        width: 100%;
    }

    .add-filter {
        width: auto !important;
        margin-bottom: 16px;
    }

    &__list {
        display: flex;
        flex-direction: column;
    }

    &__table {
        margin-bottom: 16px;
        font-size: 12px;
        float: none !important;
    }

    &__label {
        white-space: nowrap;
    }

    &__block {
        display: flex;
        align-items: center;
        column-gap: 8px;
        font-size: 12px;

        select {
            flex: 1;
        }

        &.table-header {
            column-gap: 8px;
        }
    }

    &__buttons {
        display: flex;
        align-items: stretch;
        justify-content: flex-start;
        gap: 16px;
        width: 100%;
        margin-bottom: 16px;

        &-btn {
            position: relative;
            z-index: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 30px;
            min-height: 20px;
            padding-left: 0 !important;
            border-radius: 2px;
            transition: background .3s;
            background-color: var(--button-background);
            aspect-ratio: 1/1;

            &:hover {
                background-color: #383B51;
            }

            span.icon {
                display: flex;
                justify-content: center;
                align-items: center;
                width: 100%;
                height: 100%;
                padding-left: 0 !important;
                text-align: center;
                background-position: center;

                &::before {
                    position: static;
                    top: unset;
                    left: unset;
                    transform: none;
                }
            }
        }
    }

}

#filters-table {
    width: 100%;
}

.loader {
    margin: 20px auto;
    height: 20px;
    width: 100px;
    z-index: 100;
    padding: 3px 0;
    text-align: center;
    background: rgba(253, 251, 251, 0.247);
    border: 1px solid rgb(202, 200, 200);
    border-radius: 5px;
}
</style>
